import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { User as FirebaseUser } from 'firebase/auth'
import { ReadContractErrorType } from 'viem'
import { GuestType, Membership, MembershipTemplate, Waiver } from './dbTypes'
import { TransactionType } from './dbTypes'

export interface IWalletAccount {
	address: string
	chainId: string
}

export type User = IWalletAccount | FirebaseUser | null

export enum UserRole {
	USER = 'user',
	ADMIN = 'admin'
}

export function isRoleEnough(currentRole: UserRole, requestedRole: UserRole) {
	if(currentRole === UserRole.ADMIN) return true

	return currentRole === requestedRole
}

export interface IUser {
	id?: string
	uid?: string
	email?: string
	emailVerified?: boolean
	displayName?: string
	role?: UserRole
	walletAddress?: `0x${string}`
	balance?: number
	balanceLoading?: boolean
	connectionStatus?: string
	walletError?: string
	isConnected?: boolean
	isConnectingWallet?: boolean
	loading?: boolean
	profileImageUrl?: string
	profileImageOriginalUrl?: string
}

export interface IUserContext extends IUser {
	setUserData: (data: IUser | null) => void
	setUserDataLoading: (isLoading: boolean) => void
	connectWallet: () => Promise<void>
	disconnectWallet: () => void
	updateBalance: (
		options?: RefetchOptions | undefined
	) => Promise<QueryObserverResult<bigint, ReadContractErrorType>>
}

export interface IAvailableMembership {
	id: number
	title: string
	name: string
	description: string
	price: number
	days: number
	order: number
	createdAt: string
	usdPrice?: number
	sechPrice?: number
}

export type ICachedMembershipSelection = {
	template: MembershipTemplate
	guestType: GuestType
	price: number // USD price for this guest type
	sechPrice?: number
	timestamp: number
	taxCalculation: {
		subtotal: number
		fees: Array<{
			type: string
			rate: number
			amount: number
			description: string
		}>
		total: number
	}
	appliedDiscount?: {
		discountId: number
		discountName: string
		discountType: 'flat' | 'percentage'
		discountValue: number
		calculatedAmount: number
		promoCode: string | null
	} | null
	selectedPaymentMethod?: {
		type: 'TOKEN' | 'CARD'
		cardId?: number
	} | null
}

//TODO: maybe it can replace the main Waiver type
export type AdminWaiver = Waiver & {
	hasPartner: boolean
	membershipId: number
	membership: Membership
	pdfUri: string
	phoneNumber: string
	timestamp: string
	partnerFullName: string
	profileImageUrl?: string
}

export interface AdminWaiversResponse {
	data: AdminWaiver[]
	meta: {
		currentPage: number
		pageSize: number
		total: number
	}
}

export interface IUserResponse {
	data: {
		user: IUser
		token?: string
	}
}

export interface IVerifyTokenResponse {
	customToken: string
	uid: string
	email: string
}

export const TRANSACTION_TYPES: Record<TransactionType | 'all', string> = {
	all: 'All Types',
	membership: 'Memberships',
	entry_pass: 'Entry Passes',
	order: 'Orders',
	mint: 'Minted Tokens',
	swap: 'Swapped Tokens',
	send: 'Sent Tokens',
	refund: 'Refunds',
	void: 'Voided',
	cash_conversion: 'Cash Conversions',
}